import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  TablePagination,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { Location } from "../../../../redux/types/types";

interface LocationsListProps {
  locations: Location[];
  totalLocations: number;
  page: number;
  rowsPerPage: number;
  orderBy: keyof Location | any;
  order: "asc" | "desc";
  onRequestSort: (property: keyof Location) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditLocation: (location: Location) => void;
  onDeleteLocation: (locationId: number) => void;
}

const languages =
  process.env.REACT_APP_DEFAULT_LANGUAGE === "en"
    ? (["en", "uk", "fr"] as const)
    : (["fr", "uk", "en"] as const);
type Language = (typeof languages)[number];

const LocalizedContent: React.FC<{ content: Record<Language, string> }> = ({
  content,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {languages.map(
      (lang) =>
        content[lang] && (
          <Box
            key={lang}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Chip
              label={lang.toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ width: 40, mr: 1, flexShrink: 0 }}
            />
            <Typography variant="body2" component="span">
              {content[lang]}
            </Typography>
          </Box>
        )
    )}
  </Box>
);

export default function LocationsList({
  locations,
  totalLocations,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onEditLocation,
  onDeleteLocation,
}: LocationsListProps) {
  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="location table">
          <TableHead>
            <TableRow>
              {["id", "name", "address", "email", "phone"].map((column) => (
                <TableCell
                  key={column}
                  sx={{ pl: column !== "id" ? "56px" : undefined }}
                >
                  <TableSortLabel
                    active={orderBy === column}
                    direction={orderBy === column ? order : "asc"}
                    onClick={() => onRequestSort(column as keyof Location)}
                  >
                    <FormattedMessage id={`admin.dashboard.${column}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>
                <FormattedMessage id="admin.dashboard.actions" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((location: Location) => (
              <TableRow key={location.id}>
                <TableCell>{location.id}</TableCell>
                <TableCell sx={{ pl: 1 }}>
                  <LocalizedContent content={location.name} />
                </TableCell>
                <TableCell sx={{ pl: 1 }}>
                  <LocalizedContent content={location.address} />
                </TableCell>
                <TableCell>{location.email}</TableCell>
                <TableCell>{location.phone}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => onEditLocation(location)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => onDeleteLocation(location.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalLocations}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={<FormattedMessage id="admin.dashboard.rowsPerPage" />}
      />
    </>
  );
}
